import { FormEvent, useCallback, useState } from 'react';
import { listCrmClientes } from '../../Crm/services/api.js';
import { CrmCustomerOption } from '../components/admin/ReservationFormCrmSearchModal.tsx';
import { getCrmSearchErrorMessage } from '../components/admin/reservationForm.utils.ts';

type CustomerFields = {
  name: string;
  phone: string;
  email: string;
};

type UseReservationFormCrmArgs = {
  onSelectCustomer: (fields: CustomerFields) => void;
};

export default function useReservationFormCrm({
  onSelectCustomer,
}: UseReservationFormCrmArgs) {
  const [crmSearchOpen, setCrmSearchOpen] = useState(false);
  const [crmSearchTerm, setCrmSearchTerm] = useState('');
  const [crmSearchResults, setCrmSearchResults] = useState<CrmCustomerOption[]>([]);
  const [crmSearchLoading, setCrmSearchLoading] = useState(false);
  const [crmSearchError, setCrmSearchError] = useState('');

  const openCrmSearch = useCallback((seed = '') => {
    setCrmSearchOpen(true);
    setCrmSearchTerm(seed);
    setCrmSearchResults([]);
    setCrmSearchError('');
  }, []);

  const closeCrmSearch = useCallback(() => {
    if (crmSearchLoading) return;
    setCrmSearchOpen(false);
    setCrmSearchError('');
  }, [crmSearchLoading]);

  const handleCrmSearch = useCallback(async (event?: FormEvent) => {
    event?.preventDefault();
    const search = crmSearchTerm.trim();
    if (search.length < 2) {
      setCrmSearchResults([]);
      setCrmSearchError('Digite pelo menos 2 caracteres do nome ou telefone.');
      return;
    }

    setCrmSearchLoading(true);
    setCrmSearchError('');
    try {
      const response = await listCrmClientes({
        search,
        limit: 20,
        ordenar_por: 'ultima_interacao',
        ordem: 'desc',
      });
      const clientes = Array.isArray(response?.data) ? response.data : [];
      setCrmSearchResults(clientes);
      if (!clientes.length) {
        setCrmSearchError('Nenhum cliente encontrado no CRM com essa busca.');
      }
    } catch (error: any) {
      setCrmSearchResults([]);
      setCrmSearchError(getCrmSearchErrorMessage(error));
    } finally {
      setCrmSearchLoading(false);
    }
  }, [crmSearchTerm]);

  const selectCrmCustomer = useCallback((customer: CrmCustomerOption) => {
    onSelectCustomer({
      name: customer.name || customer.nome || '',
      phone: customer.telefone || customer.phone || '',
      email: customer.email || '',
    });
    setCrmSearchOpen(false);
    setCrmSearchError('');
  }, [onSelectCustomer]);

  return {
    crmSearchOpen,
    crmSearchTerm,
    setCrmSearchTerm,
    crmSearchResults,
    crmSearchLoading,
    crmSearchError,
    openCrmSearch,
    closeCrmSearch,
    handleCrmSearch,
    selectCrmCustomer,
  };
}
